import React, { useEffect, useState } from 'react'
import { IonButton, IonContent, IonFooter, IonLabel, IonList, IonSpinner, IonToggle } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import {
  getPowerOnTimeListResults,
  setSelectedPowerOnTime,
  setFilteredMonitor,
  setFilteredFrom,
} from '../store/ekgImport.actions'
import { getEKGImportSelector } from '../selectors'
import './PowerOnTimeSelect.page.css'
import { IMonitorOption, IPowerOnTime, ITimeOption } from '../store/ekgImport.types'
import { useTranslation } from 'react-i18next'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import PowerOnTimeCard from '../powerOnTimeCard/powerOnTimeCard.component'
import { getCrewSelector } from '../../crew/selectors'
import filterIcon from '../data/FilterIcon.svg'
import cloudUploadIconWhite from '../data/CloudUploadIconWhite.svg'
import { useOfflineStatus } from '../../../utilities/isOnline.util'
import Logger from '../../../services/logger.service'

interface IPowerOnTimeSelectProps {
  onCancel: () => void
}

export interface IPowerOnTimeSelectState {
  sortedMonitorOptions: IMonitorOption[]
}

const PowerOnTimeSelect = (props: IPowerOnTimeSelectProps) => {
  const { onCancel } = props
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const isOfflineStatus = useOfflineStatus()

  const { powerOnTimeList, fromTimeOptions, monitorOptions, isLoading, filteredFrom, filteredMonitor } =
    useSelector(getEKGImportSelector)
  const { primaryMonitor } = useSelector(getCrewSelector)

  const [state, setState] = useState<IPowerOnTimeSelectState>({
    sortedMonitorOptions: [],
  })
  const { sortedMonitorOptions } = state

  const [hideImported, setHideImported] = useState(false)

  const sortMonitorOptions = () => {
    const pinnedMonitorOptions = [monitorOptions[0]]
    if (primaryMonitor) pinnedMonitorOptions.push(primaryMonitor)

    const unpinnedMonitorOptions = monitorOptions.filter((option) => {
      return option.id !== 0 && option.monitorSerial !== primaryMonitor?.monitorSerial
    })

    setState((prevState) => ({
      ...prevState,
      sortedMonitorOptions: [...pinnedMonitorOptions, ...unpinnedMonitorOptions],
    }))
  }

  const setFilterValues = () => {
    if (!filteredFrom) {
      const newFilteredFrom = fromTimeOptions[0]
      dispatch(setFilteredFrom(newFilteredFrom))
    }
    if (!filteredMonitor) {
      const newFilteredMonitor = primaryMonitor ? primaryMonitor : monitorOptions[0]
      dispatch(setFilteredMonitor(newFilteredMonitor))
    }
  }

  const onMount = () => {
    Logger.info('ECGImport: The user is on the power-on time select screen')
    sortMonitorOptions()
    setFilterValues()
    dispatch(getPowerOnTimeListResults())
  }

  useEffect(() => {
    onMount()
  }, [])

  const selectPowerOnTime = (powerOnTime: IPowerOnTime) => {
    dispatch(setSelectedPowerOnTime(powerOnTime))
  }

  useEffect(() => {
    if (isLoading || isOfflineStatus) return

    dispatch(getPowerOnTimeListResults())
  }, [filteredMonitor, filteredFrom])

  const onFromDateFilterChange = (option: ITimeOption) => {
    dispatch(setFilteredFrom(option))
  }

  const onMonitorFilterChange = (option: IMonitorOption) => {
    dispatch(setFilteredMonitor(option))
  }

  const onHideImportedToggle = (checked: boolean) => {
    Logger.info(`ECGImport: The user has toggled 'Hide previously imported' to ${checked}`)
    setHideImported(checked)
  }

  const displayedPowerOnTimeList = hideImported
    ? powerOnTimeList.filter((record) => !record.isImported)
    : powerOnTimeList

  const renderDateFilters = () => {
    return (
      <div className='pots-filter'>
        <div className='pots-filter-label'>
          <div className='pots-received-icon'>
            <img src={cloudUploadIconWhite} alt='' />
          </div>
          <label>{t('ekg.received')}</label>
        </div>
        <div className='pots-filter-select'>
          <SingleSelect
            dataTestID='Date'
            options={fromTimeOptions}
            selectedOption={filteredFrom}
            listHeader={t('ekg.filterByDateReceived')}
            action={onFromDateFilterChange}
            hideClearButton
          />
        </div>
      </div>
    )
  }

  const renderMonitorFilters = () => {
    return (
      <div className='pots-filter'>
        <div className='pots-filter-label'>
          <div className='pots-filter-icon'>
            <img src={filterIcon} alt='' />
          </div>
          <label>{t('ekg.monitors')}</label>
        </div>
        <div className='pots-filter-select'>
          <SingleSelect
            dataTestID='Monitor'
            options={sortedMonitorOptions}
            selectedOption={filteredMonitor}
            listHeader={t('ekg.filterByMonitor')}
            action={onMonitorFilterChange}
            hideClearButton
          />
        </div>
      </div>
    )
  }

  const renderHideImportedToggle = () => {
    return (
      <div className='pots-hide-imported' data-testid='HideImportedToggle'>
        <IonToggle
          id='pots-hide-imported-toggle'
          data-testid='HideImportedToggleInput'
          checked={!!hideImported}
          onIonChange={(e) => onHideImportedToggle(e.detail.checked)}
        />
        <label className='pots-hide-imported-label' htmlFor='pots-hide-imported-toggle'>
          {t('ekg.hideImportedCases')}
        </label>
      </div>
    )
  }

  const renderLoader = () => {
    return <IonSpinner data-testid='Spinner' className='pots-loader' />
  }

  const renderFooter = () => {
    return (
      <IonFooter className='ekg-footer'>
        <IonButton className='ekg-footer-button full-width' onClick={onCancel}>
          {t('common.buttons.Cancel')}
        </IonButton>
      </IonFooter>
    )
  }

  return (
    <>
      <IonContent fullscreen className='pots-content'>
        <IonList className='pots-list'>
          <div className='pots-filters'>
            {renderDateFilters()}
            {renderMonitorFilters()}
            {renderHideImportedToggle()}
          </div>

          {isLoading && renderLoader()}
          {!isLoading &&
            displayedPowerOnTimeList.length !== 0 &&
            displayedPowerOnTimeList.map((powerOnTime, i) => {
              return <PowerOnTimeCard powerOnTime={powerOnTime} key={i} handleSelect={selectPowerOnTime} />
            })}
          {!isLoading && displayedPowerOnTimeList.length === 0 && (
            <div className='no-content'>
              <IonLabel>{t('ekg.noResultsFound')}</IonLabel>
            </div>
          )}
        </IonList>
      </IonContent>
      {renderFooter()}
    </>
  )
}

export default PowerOnTimeSelect
